/* <wg-marquee>: infinite, seamless marquee used by the announcement bar.
   - Clones its content group until the track is at least twice the viewport width,
     then scrolls it with requestAnimationFrame (not a CSS animation), so the speed
     can ease down to a stop on hover/focus and ease back up on leave.
   - Respects prefers-reduced-motion (content is shown static). */
(function () {
  if (customElements.get('wg-marquee')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class WGMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.wg-marquee__track');
      this.group = this.querySelector('.wg-marquee__group');
      if (!this.track || !this.group) return;

      this.baseSpeed = parseFloat(this.getAttribute('data-speed')) || 40; /* px per second */
      this.pauseOnHover = this.getAttribute('data-pause') !== 'false';
      this.easing = 3; /* higher = quicker ease; ~1s to settle at 3 */
      this.direction = this.getAttribute('data-direction') === 'right' ? 1 : -1;

      this.offset = 0;
      this.speed = 0;
      this.targetSpeed = this.baseSpeed;
      this.lastTime = null;
      this.raf = null;

      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.tick = this.tick.bind(this);
      this.rebuild = this.rebuild.bind(this);

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
      }

      this.resizeObserver = new ResizeObserver(this.rebuild);
      this.resizeObserver.observe(this);

      document.addEventListener('visibilitychange', () => {
        this.lastTime = null;
      });

      reduceMotion.addEventListener('change', this.rebuild);
      this.rebuild();
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    rebuild() {
      /* Remove previous clones */
      this.track.querySelectorAll('.wg-marquee__group[data-clone]').forEach(function (el) {
        el.remove();
      });

      this.groupWidth = this.group.getBoundingClientRect().width;
      var viewport = this.getBoundingClientRect().width;
      if (!this.groupWidth || !viewport) return;

      /* Enough copies to cover the viewport twice, so a wrap is never visible */
      var copies = Math.max(2, Math.ceil((viewport * 2) / this.groupWidth) + 1);
      for (var i = 1; i < copies; i++) {
        var clone = this.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.querySelectorAll('a, button, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(clone);
      }

      if (reduceMotion.matches) {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
        this.track.style.transform = 'translate3d(0,0,0)';
        this.classList.add('wg-marquee--static');
        return;
      }

      this.classList.remove('wg-marquee--static');
      if (!this.raf) {
        this.lastTime = null;
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    onEnter() {
      this.targetSpeed = 0;
    }

    onLeave() {
      this.targetSpeed = this.baseSpeed;
    }

    tick(now) {
      if (this.lastTime === null) this.lastTime = now;
      var dt = Math.min((now - this.lastTime) / 1000, 0.1);
      this.lastTime = now;

      /* Exponential ease toward the target speed: gradual slow-down / speed-up */
      var k = 1 - Math.exp(-dt * this.easing);
      this.speed += (this.targetSpeed - this.speed) * k;
      if (Math.abs(this.speed) < 0.05 && this.targetSpeed === 0) this.speed = 0;

      this.offset += this.direction * this.speed * dt;

      /* Wrap seamlessly by one group width */
      if (this.offset <= -this.groupWidth) this.offset += this.groupWidth;
      if (this.offset > 0) this.offset -= this.groupWidth;

      this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  customElements.define('wg-marquee', WGMarquee);
})();
